import axios from "axios";

const ENV_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();
export const BACKEND_URL = ENV_BACKEND_URL.replace(/\/+$/, "");
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const IN_FLIGHT_MUTATIONS = new Map();

function stableSerialize(value) {
  if (value === undefined) return "";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => stableSerialize(v)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${k}:${stableSerialize(value[k])}`).join(",")}}`;
}

function mutationKey(method, url, data, config = {}) {
  const paramsSig = stableSerialize(config.params);
  const dataSig = stableSerialize(data);
  return `${method.toUpperCase()}|${url}|${paramsSig}|${dataSig}`;
}

function dedupeMutation(method, requestFn) {
  return (url, dataOrConfig, maybeConfig) => {
    let data = dataOrConfig;
    let config = maybeConfig || {};

    if (method === "delete") {
      // axios.delete signature is (url, config)
      config = dataOrConfig || {};
      data = config.data;
    }

    if (config && config.skipMutationDedupe) {
      if (method === "delete") return requestFn(url, config);
      return requestFn(url, data, config);
    }

    const key = mutationKey(method, url, data, config);
    if (IN_FLIGHT_MUTATIONS.has(key)) {
      return IN_FLIGHT_MUTATIONS.get(key);
    }

    const promise = (
      method === "delete"
        ? requestFn(url, config)
        : requestFn(url, data, config)
    ).finally(() => {
      IN_FLIGHT_MUTATIONS.delete(key);
    });

    IN_FLIGHT_MUTATIONS.set(key, promise);
    return promise;
  };
}

const rawPost = api.post.bind(api);
const rawPut = api.put.bind(api);
const rawPatch = api.patch.bind(api);
const rawDelete = api.delete.bind(api);

api.post = dedupeMutation("post", rawPost);
api.put = dedupeMutation("put", rawPut);
api.patch = dedupeMutation("patch", rawPatch);
api.delete = dedupeMutation("delete", rawDelete);

// Attach token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("erp_token");
  const headers = config.headers || {};

  if (token) {
    config.headers = { ...headers, Authorization: `Bearer ${token}` };
  } else {
    config.headers = headers;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== "/login") {
        localStorage.removeItem("erp_token");
        localStorage.removeItem("erp_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
